/* eslint-disable */
// @ts-nocheck
// @ts-ignore
/**
 * Web-Highlighter based highlighting implementation
 * Using web-highlighter library for efficient text highlighting with built-in functions only
 * Follows web-highlighter documentation strictly - no text storage for efficiency
 */

// Import web-highlighter using dynamic import to handle module resolution
let Highlighter = null;

// Load web-highlighter dynamically
const loadWebHighlighter = async () => {
  if (Highlighter) return Highlighter;

  try {
    // Try different import methods
    const module = await import("web-highlighter");
    Highlighter = module.default || module.Highlighter || module;

    if (!Highlighter || typeof Highlighter !== "function") {
      throw new Error("Web-highlighter not found or not a constructor");
    }

    return Highlighter;
  } catch (error) {
    console.error("Failed to load web-highlighter:", error);
    throw error;
  }
};


/**
 * Efficient highlight data structure using web-highlighter's Source object design
 * No text storage for maximum efficiency - only positional metadata
 * NOTE: This class is for conceptual understanding of highlight structure.
 * The actual highlights stored in the Zustand store will contain both backend ID (as 'id')
 * and the web-highlighter's ID (inside 'highlightData.id').
 * The WebHighlighterWrapper will primarily use the 'highlightData.id' for its internal operations.
 */
class HighlightSource {
  constructor(startMeta, endMeta, colorId, id = null, content = {}) { // Added content for consistency
    this.id = id || this.generateId(); // This ID is web-highlighter's internal/frontend ID
    this.startMeta = startMeta;
    this.endMeta = endMeta;
    this.colorId = colorId;
    this.createdAt = new Date().toISOString();
    this.content = content; // Added for any comment/emoji association
  }

  generateId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 11);
    return `highlight-${timestamp}-${random}`;
  }

  toJSON() {
    return {
      id: this.id, // Frontend ID
      startMeta: this.startMeta,
      endMeta: this.endMeta,
      colorId: this.colorId,
      createdAt: this.createdAt,
      content: this.content,
    };
  }

  static fromJSON(data) {
    const highlight = new HighlightSource(
      data.startMeta,
      data.endMeta,
      data.colorId,
      data.id,
      data.content
    );
    highlight.createdAt = data.createdAt;
    return highlight;
  }
}

/**
 * Web-Highlighter wrapper class implementing selective highlight replacement
 * Uses only web-highlighter built-in functions as per documentation
 * This wrapper will manage highlights internally using the frontend-generated IDs.
 * The mapping to backend IDs happens in the Zustand store.
 */
class WebHighlighterWrapper {
  constructor(
    container,
    onHighlightClick,
    onSelectionCreate,
    getCurrentColor,
    onHighlightRemove
  ) {
    this.container = container;
    this.onHighlightClick = onHighlightClick;
    this.onSelectionCreate = onSelectionCreate;
    this.getCurrentColor = getCurrentColor;
    this.onHighlightRemove = onHighlightRemove;
    this.highlighter = null;
    this.highlightStore = new Map(); // Stores highlight metadata by WEB-HIGHLIGHTER ID (frontend ID)
    this.isRunning = false;
    this.selectionHandler = null;
    this.floatingButton = null;
    this.currentSelection = null;
  }

  /**
   * Initialize web-highlighter with proper configuration
   */
  async initialize() {
    try {
      // Load web-highlighter dynamically
      const HighlighterClass = await loadWebHighlighter();

      // Create web-highlighter instance with configuration
      this.highlighter = new HighlighterClass({
        $root: this.container,
        exceptSelectors: ["pre", "code", "script", "style"],
        wrapTag: "span",
        verbose: false,
        style: {
          className: "web-highlight-wrap",
        },
      });

      // Set up event listeners using web-highlighter's built-in events
      this.setupEventListeners();

      return true;
    } catch (error) {
      console.error("Failed to initialize Web-Highlighter:", error);
      return false;
    }
  }

  /**
   * Set up event listeners using web-highlighter's event system
   */
  setupEventListeners() {
    // Get the event constants from the loaded Highlighter class
    const EventType = Highlighter.event;

    this.highlighter.on(EventType.CREATE, (data, inst, e) => {
      this.handleHighlightCreate(data);
    });

    this.highlighter.on(EventType.CLICK, (data, inst, e) => {
      if (this.onHighlightClick) {
        // data.id here is the web-highlighter's ID
        this.onHighlightClick(data);
      }
    });

    this.highlighter.on(EventType.HOVER, (data, inst, e) => {
      this.highlighter.addClass("highlight-hover", data.id);
    });

    this.highlighter.on(EventType.HOVER_OUT, (data, inst, e) => {
      this.highlighter.removeClass("highlight-hover", data.id);
    });

    this.highlighter.on(EventType.REMOVE, (data, inst, e) => {
      if (data.ids) {
        // data.ids here are the web-highlighter's IDs
        data.ids.forEach((id) => this.highlightStore.delete(id));
      }
      // Note: We don't call onHighlightRemove here directly, as it's handled
      // by the floating button's unhighlight logic for explicit user action.
      // If highlights can be removed by other means (e.g., system-level),
      // this event might need to be propagated differently.
    });
  }

  /**
   * Handle highlight creation from user selection.
   * This is where a new highlight (with a web-highlighter generated ID) is first processed.
   */
  handleHighlightCreate(data) {
    const newHighlightSource = data.sources[0]; // This `source` object has the web-highlighter generated ID.

    // If this highlight originated from a deserialization (restoring from store)
    // or is a 'split-' highlight (internally generated by the wrapper for overlaps),
    // we don't need to send it to the backend as a *new* highlight.
    // We just ensure its internal state and styling are correct.
    if (data.type === "from-store" || newHighlightSource.id.startsWith("split-")) {
      console.log(`⏭️ Skipping backend API call for 'from-store' or 'split-' highlight: ${newHighlightSource.id}`);
      // For restored highlights, use their original stored color, not the current selected color
      const storedHighlight = this.highlightStore.get(newHighlightSource.id);
      const originalColorId = storedHighlight?.colorId || newHighlightSource.colorId;
      
      if (originalColorId) {
        // Ensure the highlight is styled with its original color
        this.applyColorStyling(newHighlightSource.id, originalColorId);
        // Update internal store for consistency (though it should already be there for from-store)
        this.highlightStore.set(newHighlightSource.id, {
          ...newHighlightSource,
          colorId: originalColorId, // Use original color, not current selected color
        });
      }
      return;
    }

    // For new highlights, get the current selected color
    const currentColor = this.getCurrentColor();
    if (!currentColor) {
      console.warn("⚠️ No color selected for highlight.");
      return;
    }

    console.log(
      `🎯 Processing new highlight from selection: (frontend ID: ${newHighlightSource.id})`
    );

    // This `highlightDataForStore` object has the web-highlighter's `id` (frontend ID).
    // This is what will be passed to `onSelectionCreate` (MainContent) and then `addHighlight` (Zustand store).
    const highlightDataForStore = new HighlightSource(
      newHighlightSource.startMeta,
      newHighlightSource.endMeta,
      currentColor.id, // Use the currently selected color
      newHighlightSource.id, // Web-highlighter's ID
      {} // Empty content for now, can be updated later
    ).toJSON(); // Convert to plain object for storage

    // Store this in the wrapper's internal map using the frontend ID.
    this.highlightStore.set(newHighlightSource.id, highlightDataForStore);

    // Apply color styling immediately.
    this.applyColorStyling(newHighlightSource.id, currentColor.id);

    // Notify parent component (MainContent) to save to backend.
    // The `onSelectionCreate` will receive this object, extract its `id` as frontend ID,
    // save to backend, and the backend will return a new backend `id`.
    if (this.onSelectionCreate) {
      this.onSelectionCreate({
        sources: [highlightDataForStore], // Pass the web-highlighter's data structure
      });
    }

    // Consolidate adjacent highlights of the same color (if any)
    // This logic needs to be carefully managed if backend storage also consolidates.
    // For now, let's assume backend stores individual highlights.
    // this.consolidateAdjacentHighlights(newHighlightSource); // Temporarily commented out to simplify ID management
  }

  /**
   * Find overlapping highlights in our stored data (not DOM-based)
   * This operates on the wrapper's `highlightStore` which uses frontend IDs.
   */
  findOverlappingStoredHighlights(newHighlight) {
    const overlapping = [];

    for (const [id, existingHighlight] of this.highlightStore) {
      if (this.highlightsOverlap(existingHighlight, newHighlight)) {
        overlapping.push(existingHighlight);
      }
    }

    return overlapping;
  }

  /**
   * Check if two highlights overlap based on their text offsets
   */
  highlightsOverlap(highlight1, highlight2) {
    // Check if they're in the same parent element
    if (
      highlight1.startMeta.parentTagName !==
      highlight2.startMeta.parentTagName ||
      highlight1.startMeta.parentIndex !== highlight2.startMeta.parentIndex
    ) {
      return false;
    }

    const start1 = highlight1.startMeta.textOffset;
    const end1 = highlight1.endMeta.textOffset;
    const start2 = highlight2.startMeta.textOffset;
    const end2 = highlight2.endMeta.textOffset;

    // Check for overlap: start1 < end2 && start2 < end1
    return start1 < end2 && start2 < end1;
  }

  /**
   * Handle overlapping highlight by splitting the existing one
   * NOTE: This is a complex selective replacement logic.
   * If the backend handles merging/splitting highlights, this frontend logic
   * might need to be simplified or removed, or coordinated closely with backend.
   * For a basic MVP, a simple "new highlight replaces old if overlaps significantly" might be enough.
   * Here, it's attempting to manage internal web-highlighter splits.
   */
  handleOverlappingHighlight(existingHighlight, newHighlight, newColor) {
    // Current implementation of handleOverlappingHighlight is complex for web-highlighter internal IDs.
    // To simplify and focus on the ID sync issue, we might want to reconsider this,
    // or ensure that `createSplitHighlight` also calls `onSelectionCreate` with its new IDs.
    // For now, I will keep the original logic, assuming it's intended for client-side visual management.

    const existingStart = existingHighlight.startMeta.textOffset;
    const existingEnd = existingHighlight.endMeta.textOffset;
    const newStart = newHighlight.startMeta.textOffset;
    const newEnd = newHighlight.endMeta.textOffset;

    // If new highlight completely contains existing one, just update the color
    if (newStart <= existingStart && newEnd >= existingEnd) {
      this.highlighter.removeClass(
        `highlight-${existingHighlight.colorId}`,
        existingHighlight.id
      );
      this.highlighter.addClass(
        `highlight-${newColor.id}`,
        existingHighlight.id
      );

      // Update stored highlight data in wrapper's internal store
      existingHighlight.colorId = newColor.id;
      this.highlightStore.set(existingHighlight.id, existingHighlight);

      // Notify parent component that this highlight's color has changed (triggers update in store)
      if (this.onSelectionCreate) { // Re-using onSelectionCreate for an "update" semantic
        this.onSelectionCreate({
          sources: [{ ...existingHighlight, colorId: newColor.id }],
        });
      }
      return;
    }

    // If existing highlight completely contains new one, we need to split it
    if (existingStart <= newStart && existingEnd >= newEnd) {
      this.highlighter.remove(existingHighlight.id); // Remove original from DOM
      this.highlightStore.delete(existingHighlight.id); // Remove original from internal store

      // Create three portions: before (original color), middle (new color), after (original color)
      if (existingStart < newStart) {
        const beforeHighlight = new HighlightSource(
          existingHighlight.startMeta,
          { ...existingHighlight.endMeta, textOffset: newStart },
          existingHighlight.colorId
        ).toJSON();
        this.createSplitHighlight(beforeHighlight);
      }

      const middleHighlight = new HighlightSource(
        { ...existingHighlight.startMeta, textOffset: newStart },
        { ...existingHighlight.endMeta, textOffset: newEnd },
        newColor.id, // New color for the middle part
        newHighlight.id // Use the newHighlight's ID for the middle part
      ).toJSON();
      this.createSplitHighlight(middleHighlight);

      if (newEnd < existingEnd) {
        const afterHighlight = new HighlightSource(
          { ...existingHighlight.startMeta, textOffset: newEnd },
          existingHighlight.endMeta,
          existingHighlight.colorId
        ).toJSON();
        this.createSplitHighlight(afterHighlight);
      }
      return;
    }

    // For partial overlaps, keep the non-overlapping portion with original color
    this.highlighter.remove(existingHighlight.id);
    this.highlightStore.delete(existingHighlight.id);

    if (existingStart < newStart) {
      const beforeHighlight = new HighlightSource(
        existingHighlight.startMeta,
        { ...existingHighlight.endMeta, textOffset: newStart },
        existingHighlight.colorId
      ).toJSON();
      this.createSplitHighlight(beforeHighlight);
    }

    if (newEnd < existingEnd) {
      const afterHighlight = new HighlightSource(
        { ...existingHighlight.startMeta, textOffset: newEnd },
        existingHighlight.endMeta,
        existingHighlight.colorId
      ).toJSON();
      this.createSplitHighlight(afterHighlight);
    }

    // Also need to create the new highlight if it's not fully contained by existing one
    // and hasn't been added yet (this path means it's a 'new' creation that overlaps).
    const highlightDataForStore = new HighlightSource(
      newHighlight.startMeta,
      newHighlight.endMeta,
      newColor.id,
      newHighlight.id
    ).toJSON();
    this.createSplitHighlight(highlightDataForStore); // Treat the new highlight as a "split" creation too for consistent handling
  }

  /**
   * Create a split highlight portion (NO TEXT STORAGE)
   * This function internally manages web-highlighter's IDs.
   * If these split highlights also need to be persisted, they must be sent to the backend.
   */
  createSplitHighlight(highlightData) {
    // Store the split highlight in the wrapper's internal map (frontend ID).
    this.highlightStore.set(highlightData.id, highlightData);

    // Extract text from DOM for web-highlighter, but don't store it.
    const extractedText = this.extractTextFromRange(
      highlightData.startMeta,
      highlightData.endMeta
    );

    // Use web-highlighter's `fromStore` to render it in the DOM.
    this.highlighter.fromStore(
      highlightData.startMeta,
      highlightData.endMeta,
      extractedText,
      highlightData.id // Use the frontend ID here
    );

    // Apply the original color.
    this.applyColorStyling(highlightData.id, highlightData.colorId);

    // Notify parent component if this split part needs to be sent to backend.
    // This is crucial: if splitting means new highlights, the backend needs to know.
    // The `onSelectionCreate` (MainContent's `handleSelectionCreate`) will then call `addHighlight` in the store.
    if (this.onSelectionCreate) {
      this.onSelectionCreate({
        sources: [highlightData],
      });
    }
  }

  /**
   * Extract text from DOM range for web-highlighter (not stored)
   */
  extractTextFromRange(startMeta, endMeta) {
    try {
      // Find the parent element
      const parentElements = this.container.getElementsByTagName(
        startMeta.parentTagName
      );

      // Fallback if index is out of bounds or element not found directly
      let parentElement = null;
      if (parentElements.length > startMeta.parentIndex) {
        parentElement = parentElements[startMeta.parentIndex];
      } else {
        // Try to find a containing element if exact match fails, heuristic
        // This is a robust attempt, but still might fail on very dynamic DOM.
        const allElements = this.container.querySelectorAll(startMeta.parentTagName);
        // Find the Nth element that could match, or just the first that looks plausible
        if (allElements.length > 0) {
          parentElement = allElements[Math.min(startMeta.parentIndex, allElements.length - 1)];
          console.warn(`⚠️ Exact parent element index failed. Falling back to element at index ${Math.min(startMeta.parentIndex, allElements.length - 1)} for ${startMeta.parentTagName}.`);
        }
      }

      if (!parentElement) {
        console.warn(`⚠️ Cannot find element: ${startMeta.parentTagName}[${startMeta.parentIndex}]`);
        return "";
      }

      const fullText = parentElement.textContent || "";

      // Validate offsets
      // It's possible textContent has changed, so offsets might be out of sync.
      const actualEndOffset = Math.min(endMeta.textOffset, fullText.length);
      const actualStartOffset = Math.min(startMeta.textOffset, actualEndOffset);

      if (actualStartOffset > actualEndOffset) {
        console.warn(`⚠️ Invalid text offsets after adjustment: start=${actualStartOffset}, end=${actualEndOffset}, textLength=${fullText.length}. Returning empty string.`);
        return "";
      }

      const extractedText = fullText.substring(actualStartOffset, actualEndOffset);
      return extractedText;
    } catch (error) {
      console.warn("⚠️ Failed to extract text from range:", error);
      console.warn("StartMeta:", startMeta);
      console.warn("EndMeta:", endMeta);
      return "";
    }
  }

  /**
   * Generate a unique ID for split highlights (frontend ID)
   */
  generateUniqueId() {
    return (
      "split-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now()
    );
  }

  /**
   * Apply color styling using web-highlighter's built-in addClass method
   * Uses the frontend `highlightId`.
   */
  applyColorStyling(highlightId, colorId) {
    // First, remove all possible color classes to prevent accumulation
    const allColorIds = ['yellow', 'green', 'blue', 'pink', 'purple'];
    allColorIds.forEach(color => {
      try {
        this.highlighter.removeClass(`highlight-${color}`, highlightId);
      } catch (error) {
        // Ignore errors if class doesn't exist
      }
    });

    // Add the new color class
    const colorClass = `highlight-${colorId}`;
    this.highlighter.addClass(colorClass, highlightId);

    // Also add the ID as a class for easier selection in the DOM
    this.highlighter.addClass(highlightId, highlightId);
  }

  /**
   * Consolidate adjacent highlights of the same color
   * NOTE: This is client-side consolidation. If backend doesn't do this,
   * it might lead to many small highlights in the backend data.
   */
  consolidateAdjacentHighlights(newHighlight) {
    // This logic relies on the wrapper's internal highlightStore, which uses frontend IDs.
    // If consolidation happens, the new consolidated highlight also needs its own
    // frontend ID and needs to be reported to the backend as an update/delete/create.
    // For now, temporarily commenting this out to avoid extra complexity with ID mapping
    // during the immediate highlight creation flow.
    /*
    const adjacentHighlights = this.findAdjacentHighlights(newHighlight);

    if (adjacentHighlights.length === 0) {
      return;
    }

    console.log(
      `🔗 Found ${adjacentHighlights.length} adjacent highlights to consolidate`
    );

    const allHighlights = [newHighlight, ...adjacentHighlights].sort(
      (a, b) => a.startMeta.textOffset - b.startMeta.textOffset
    );

    const consolidatedStart = Math.min(
      ...allHighlights.map((h) => h.startMeta.textOffset)
    );
    const consolidatedEnd = Math.max(
      ...allHighlights.map((h) => h.endMeta.textOffset)
    );

    // Remove all existing highlights from DOM and internal store
    allHighlights.forEach((highlight) => {
      this.highlighter.remove(highlight.id);
      this.highlightStore.delete(highlight.id);
    });

    // Create a new consolidated highlight using a new frontend ID
    const consolidatedHighlight = new HighlightSource(
      { ...newHighlight.startMeta, textOffset: consolidatedStart },
      { ...newHighlight.endMeta, textOffset: consolidatedEnd },
      newHighlight.colorId
    ).toJSON(); // Generate new ID

    // Create the consolidated highlight in DOM
    const extractedText = this.extractTextFromRange(
      consolidatedHighlight.startMeta,
      consolidatedHighlight.endMeta
    );
    this.highlighter.fromStore(
      consolidatedHighlight.startMeta,
      consolidatedHighlight.endMeta,
      extractedText,
      consolidatedHighlight.id
    );

    // Apply color styling
    this.applyColorStyling(
      consolidatedHighlight.id,
      consolidatedHighlight.colorId
    );

    // Store the consolidated highlight in wrapper's internal store
    this.highlightStore.set(consolidatedHighlight.id, consolidatedHighlight);

    // Notify parent component (this implies deletion of old + creation of new consolidated)
    if (this.onHighlightRemove) {
        this.onHighlightRemove(allHighlights.map(h => h.id)); // Report old IDs as removed
    }
    if (this.onSelectionCreate) {
      this.onSelectionCreate({
        sources: [consolidatedHighlight], // Report new consolidated ID as created
      });
    }

    console.log(
      `✅ Consolidated highlights into range (${consolidatedStart}-${consolidatedEnd}) with frontend ID: ${consolidatedHighlight.id}`
    );
    */
  }

  /**
   * Find adjacent highlights of the same color
   */
  findAdjacentHighlights(targetHighlight) {
    const adjacent = [];
    const targetStart = targetHighlight.startMeta.textOffset;
    const targetEnd = targetHighlight.endMeta.textOffset;
    const targetColor = targetHighlight.colorId;

    for (const [id, highlight] of this.highlightStore) {
      if (id === targetHighlight.id) {
        continue;
      }

      if (
        highlight.startMeta.parentTagName ===
        targetHighlight.startMeta.parentTagName &&
        highlight.startMeta.parentIndex ===
        targetHighlight.startMeta.parentIndex &&
        highlight.colorId === targetColor
      ) {
        const highlightStart = highlight.startMeta.textOffset;
        const highlightEnd = highlight.endMeta.textOffset;

        if (
          highlightEnd === targetStart ||
          highlightStart === targetEnd ||
          (highlightStart < targetEnd && highlightEnd > targetStart)
        ) {
          adjacent.push(highlight);
        }
      }
    }
    return adjacent;
  }

  /**
   * Get highlight ID from metadata (fallback method)
   */
  getHighlightIdFromMeta(highlight) {
    // In this wrapper, `highlight.id` should always be the frontend ID.
    // This method might be redundant if we consistently use `highlight.id`.
    return highlight.id;
  }

  /**
   * Start the highlighter with floating button support
   */
  run() {
    this.isRunning = true;
    this.highlighter.run();
    this.setupSelectionListener();
    console.log(
      "🚀 Web-Highlighter is now running with floating button support"
    );
  }

  /**
   * Stop the highlighter
   */
  stop() {
    this.isRunning = false;
    this.highlighter.stop();
    this.removeSelectionListener();
    this.hideFloatingButton();
    console.log("⏹️ Web-Highlighter stopped");
  }

  /**
   * Setup selection change listener for floating button
   */
  setupSelectionListener() {
    this.boundSelectionChange = this.handleSelectionChange.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundDocumentClick = this.handleDocumentClick.bind(this);

    document.addEventListener("selectionchange", this.boundSelectionChange);
    document.addEventListener("mouseup", this.boundMouseUp);
    document.addEventListener("click", this.boundDocumentClick);
  }

  /**
   * Remove selection listener
   */
  removeSelectionListener() {
    if (this.boundSelectionChange) {
      document.removeEventListener(
        "selectionchange",
        this.boundSelectionChange
      );
    }
    if (this.boundMouseUp) {
      document.removeEventListener("mouseup", this.boundMouseUp);
    }
    if (this.boundDocumentClick) {
      document.removeEventListener("click", this.boundDocumentClick);
    }
  }

  /**
   * Handle selection changes to show/hide floating button
   */
  handleSelectionChange() {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this.hideFloatingButton();
      this.currentSelection = null;
      return;
    }

    const range = selection.getRangeAt(0);

    if (!this.container.contains(range.commonAncestorContainer)) {
      this.hideFloatingButton();
      this.currentSelection = null;
      return;
    }

    if (this.selectionContainsHighlights(range)) {
      this.currentSelection = range.cloneRange();
      this.highlighter.stop(); // Temporarily stop web-highlighter for floating button
      this.showFloatingButton(range);
    } else {
      this.hideFloatingButton();
      this.currentSelection = null;
      if (this.isRunning) {
        this.highlighter.run(); // Restart web-highlighter for normal highlighting
      }
    }
  }

  /**
   * Handle mouse up to position floating button correctly
   */
  handleMouseUp(event) {
    setTimeout(() => {
      if (this.currentSelection) {
        this.positionFloatingButton(this.currentSelection);
      }
    }, 50);
  }

  /**
   * Handle document clicks to hide floating button when clicking outside
   */
  handleDocumentClick(event) {
    if (this.floatingButton && this.floatingButton.contains(event.target)) {
      return;
    }

    if (this.floatingButton && this.floatingButton.style.display === "block") {
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
          this.hideFloatingButton();
          this.currentSelection = null;
        }
      }, 100);
    }
  }

  /**
   * Check if selection contains highlighted content
   */
  selectionContainsHighlights(range) {
    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.classList && node.classList.contains("web-highlight-wrap")) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      if (range.intersectsNode(node)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Show floating button for unhighlighting
   */
  showFloatingButton(range) {
    if (!this.floatingButton) {
      this.createFloatingButton();
    }
    this.floatingButton.style.display = "block";
    this.positionFloatingButton(range);
  }

  /**
   * Hide floating button and restart web-highlighter
   */
  hideFloatingButton() {
    if (this.floatingButton) {
      this.floatingButton.style.display = "none";
    }
    if (this.isRunning) {
      this.highlighter.run();
    }
  }

  /**
   * Create floating button element
   */
  createFloatingButton() {
    this.floatingButton = document.createElement("div");
    this.floatingButton.className = "web-highlighter-floating-button";
    this.floatingButton.innerHTML = `
      <button class="unhighlight-btn" title="Remove highlight">
        🗑️ Unhighlight
      </button>
    `;

    this.floatingButton.style.cssText = `
      position: absolute;
      z-index: 10000;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      padding: 4px;
      display: none;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const button = this.floatingButton.querySelector(".unhighlight-btn");
    button.style.cssText = `
      background: #ff4757;
      color: white;
      border: none;
      padding: 6px 12px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    `;

    button.addEventListener("mouseenter", () => {
      button.style.background = "#ff3742";
    });

    button.addEventListener("mouseleave", () => {
      button.style.background = "#ff4757";
    });

    button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleUnhighlight();
    });

    document.body.appendChild(this.floatingButton);
  }

  /**
   * Position floating button near the selection
   */
  positionFloatingButton(range) {
    if (!this.floatingButton) return;

    const rect = range.getBoundingClientRect();
    const buttonHeight = this.floatingButton.offsetHeight || 40;
    const buttonWidth = this.floatingButton.offsetWidth || 120;

    let top = rect.top - buttonHeight - 5;
    let left = rect.left + rect.width / 2 - buttonWidth / 2;

    if (top < 0) {
      top = rect.bottom + 5;
    }

    if (left < 0) {
      left = 5;
    } else if (left + buttonWidth > window.innerWidth) {
      left = window.innerWidth - buttonWidth - 5;
    }

    top += window.scrollY;
    left += window.scrollX;

    this.floatingButton.style.top = `${top}px`;
    this.floatingButton.style.left = `${left}px`;
  }

  /**
   * Handle unhighlight action - complete highlight removal
   * Modified to prevent unwanted highlight creation on adjacent text
   */
  handleUnhighlight() {
    if (!this.currentSelection) return;

    console.log("🗑️ Starting complete highlight removal");

    try {
      // Find highlight IDs that overlap with the selection (these are frontend IDs)
      const overlappingHighlightIds = this.findHighlightsInRange(
        this.currentSelection
      );

      console.log("🗑️ Found overlapping highlights (frontend IDs):", overlappingHighlightIds);

      if (overlappingHighlightIds.length === 0) {
        console.log("⚠️ No highlights found in selection for unhighlighting");
        this.hideFloatingButton();
        return;
      }

      const removedFrontendIds = [];

      overlappingHighlightIds.forEach((frontendId) => {
        // Get the highlight data from wrapper's internal store (which has frontend ID)
        const highlightData = this.highlightStore.get(frontendId);
        if (!highlightData) {
          console.warn(`⚠️ No internal data found for highlight: ${frontendId}`);
          return;
        }

        // Remove the entire highlight from DOM and internal store
        // No longer calculating preserved portions to prevent unwanted highlight creation
        this.highlighter.remove(frontendId);
        this.highlightStore.delete(frontendId);
        removedFrontendIds.push(frontendId);
      });

      // Notify parent component about the highlights that were explicitly removed (frontend IDs)
      if (this.onHighlightRemove && removedFrontendIds.length > 0) {
        this.onHighlightRemove(removedFrontendIds);
      }

      // Clear selection and hide button
      window.getSelection().removeAllRanges();
      this.hideFloatingButton();
      this.currentSelection = null;

      if (this.isRunning) {
        this.highlighter.run();
      }
    } catch (error) {
      console.error("❌ Error during highlight removal:", error);
    }
  }


  /**
   * Find highlight IDs (frontend IDs) that intersect with the given range
   */
  findHighlightsInRange(range) {
    const highlightIds = new Set(); // Use a Set to avoid duplicates
    // console.log("🔍 Finding highlights in range:", range.toString());

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          if (node.classList && node.classList.contains("web-highlight-wrap")) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      if (range.intersectsNode(node)) {
        const id = this.getHighlightIdFromElement(node); // Get frontend ID
        if (id) {
          highlightIds.add(id);
        }
      }
    }
    return Array.from(highlightIds);
  }

  /**
   * Get highlight ID (frontend ID) from DOM element
   */
  getHighlightIdFromElement(element) {
    if (element.dataset && element.dataset.highlightId) {
      return element.dataset.highlightId;
    }
    try {
      const id = this.highlighter.getIdByDom(element);
      if (id) return id;
    } catch (error) {
      // console.warn("⚠️ Could not get highlight ID from element using highlighter.getIdByDom:", error);
    }
    // Fallback: search our internal store (which maps frontend IDs)
    for (const [storedId, highlightData] of this.highlightStore) {
      try {
        const highlightElements = this.highlighter.getDoms(storedId);
        if (highlightElements && Array.isArray(highlightElements) && highlightElements.includes(element)) {
          return storedId;
        }
      } catch (error) {
        // Continue checking other highlights
      }
    }
    return null;
  }

  /**
   * Selectively remove highlight portion that overlaps with selection
   * This function is no longer needed as `handleUnhighlight` manages splitting.
   * Keeping it here for reference/if a different removal strategy is used.
   */
  // selectivelyRemoveHighlight(highlightId, selectionRange) { ... }

  /**
   * Calculate preserved portions when selectively removing a highlight
   */
  calculatePreservedPortions(highlightData, selectionRange) {
    const preservedPortions = [];

    try {
      const parentElement = this.container.getElementsByTagName(
        highlightData.startMeta.parentTagName
      )[highlightData.startMeta.parentIndex];

      if (!parentElement) {
        console.warn("⚠️ Parent element not found for highlight, cannot calculate preserved portions.");
        return preservedPortions;
      }

      // Get selection boundaries relative to the parent element's text content
      const selectionStart = this.getTextOffsetInElement(
        parentElement,
        selectionRange.startContainer,
        selectionRange.startOffset
      );
      const selectionEnd = this.getTextOffsetInElement(
        parentElement,
        selectionRange.endContainer,
        selectionRange.endOffset
      );

      // Get highlight boundaries
      const highlightStart = highlightData.startMeta.textOffset;
      const highlightEnd = highlightData.endMeta.textOffset;

      // Create before portion if it exists and is outside selection
      if (highlightStart < selectionStart) {
        const beforePortion = new HighlightSource(
          highlightData.startMeta,
          { ...highlightData.endMeta, textOffset: selectionStart },
          highlightData.colorId
        ).toJSON();
        preservedPortions.push(beforePortion);
      }

      // Create after portion if it exists and is outside selection
      if (selectionEnd < highlightEnd) {
        const afterPortion = new HighlightSource(
          { ...highlightData.startMeta, textOffset: selectionEnd },
          highlightData.endMeta,
          highlightData.colorId
        ).toJSON();
        preservedPortions.push(afterPortion);
      }

    } catch (error) {
      console.warn("⚠️ Error calculating preserved portions:", error);
    }

    return preservedPortions;
  }

  /**
   * Get text offset within a specific parent element
   */
  getTextOffsetInElement(parentElement, node, offset) {
    if (node === parentElement) {
      return offset;
    }

    if (node.nodeType === Node.TEXT_NODE && parentElement.contains(node)) {
      let textOffset = offset;
      let current = node.previousSibling;
      while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
          textOffset += current.textContent.length;
        } else if (current.nodeType === Node.ELEMENT_NODE) {
          textOffset += current.textContent.length; // Approximate for embedded elements
        }
        current = current.previousSibling;
      }
      return textOffset;
    }

    // Fallback for elements or complex structures
    // This is more robust but potentially slower for large documents
    const range = document.createRange();
    range.selectNodeContents(parentElement);
    range.setEnd(node, offset);
    return range.toString().length;
  }

  /**
   * Remove a highlight by its frontend ID using web-highlighter's built-in remove
   */
  removeHighlight(frontendHighlightId) {
    try {
      this.highlighter.remove(frontendHighlightId);
      this.highlightStore.delete(frontendHighlightId);
      console.log(`🗑️ Removed highlight from DOM and internal store: ${frontendHighlightId}`);
      return true;
    } catch (error) {
      console.error("Failed to remove highlight from DOM/internal store:", error);
      return false;
    }
  }

  /**
   * Remove all highlights using web-highlighter's built-in removeAll
   */
  removeAll() {
    try {
      this.highlighter.removeAll();
      this.highlightStore.clear();
      console.log("🗑️ Removed all highlights from DOM and internal store.");
      return true;
    } catch (error) {
      console.error("Failed to remove all highlights:", error);
      return false;
    }
  }

  /**
   * Get all highlights using web-highlighter's built-in getAll
   * This would return the DOM-generated highlights with frontend IDs.
   */
  getAllHighlights() {
    try {
      return this.highlighter.getAll();
    } catch (error) {
      console.error("❌ Failed to get all highlights from DOM:", error);
      return [];
    }
  }

  /**
   * Serialize highlights for storage - return array of highlight objects (frontend-ID based)
   */
  serialize() {
    console.log(`📦 Serializing ${this.highlightStore.size} highlights from internal store (frontend IDs)`);
    // Return a copy of the values from the internal map
    return Array.from(this.highlightStore.values());
  }

  /**
   * Deserialize and restore highlights using web-highlighter's built-in fromStore.
   * `highlights` array contains objects with backend ID (`highlight.id`) AND
   * frontend ID (`highlight.highlightData.id`).
   */
  deserialize(highlights) {
    try {
      if (!highlights || !Array.isArray(highlights) || highlights.length === 0) {
        console.log("📭 No highlights to restore.");
        return true;
      }

      const deduplicatedHighlights = this.deduplicateHighlights(highlights);

      console.log(
        `🔄 Restoring ${deduplicatedHighlights.length} unique highlights to DOM.`
      );

      deduplicatedHighlights.forEach((highlight) => {
        try {
          // IMPORTANT: Use the frontend ID (from highlightData.id) for web-highlighter operations.
          const frontendHighlightId = highlight.highlightData?.id;

          if (!frontendHighlightId || !highlight.startMeta || !highlight.endMeta) {
            console.warn(`⚠️ Skipping highlight due to missing required properties (frontend ID, startMeta, or endMeta):`, highlight);
            return;
          }

          // Extract text from DOM for web-highlighter (not stored)
          const extractedText = this.extractTextFromRange(
            highlight.startMeta,
            highlight.endMeta
          );

          // Use web-highlighter's fromStore method
          this.highlighter.fromStore(
            highlight.startMeta,
            highlight.endMeta,
            extractedText,
            frontendHighlightId // Use the frontend ID here
          );

          // Apply the correct color styling
          this.applyColorStyling(frontendHighlightId, highlight.colorId);

          // Update the wrapper's internal highlightStore with this frontend ID
          this.highlightStore.set(frontendHighlightId, {
            id: frontendHighlightId, // Key and ID is frontend ID
            startMeta: highlight.startMeta,
            endMeta: highlight.endMeta,
            colorId: highlight.colorId,
            createdAt: highlight.createdAt,
            // You might want to store the backend ID here too for reverse lookup if needed by the wrapper
            backendId: highlight.id,
          });

          // console.log(`✅ Restored highlight: Backend ID: ${highlight.id}, Frontend ID: ${frontendHighlightId}`);
        } catch (error) {
          console.warn(`⚠️ Failed to restore highlight (backend ID: ${highlight.id}):`, error);
        }
      });

      console.log("✅ All highlights restoration completed in DOM.");
      return true;
    } catch (error) {
      console.error("❌ Failed to deserialize highlights:", error);
      return false;
    }
  }

  /**
   * Deduplicate highlights by backend ID, keeping the latest version of each highlight.
   * This is used when receiving highlights from the store/API.
   */
  deduplicateHighlights(highlights) {
    const highlightMap = new Map(); // Maps backendId -> highlight object

    highlights.forEach((highlight) => {
      // Ensure highlight has a backend ID for deduplication
      if (!highlight.id) {
        console.warn('Skipping highlight without a backend ID during deduplication:', highlight);
        return;
      }

      const existingHighlight = highlightMap.get(highlight.id);

      // Keep the highlight with the latest createdAt timestamp
      // Assuming highlight.createdAt is consistently available and comparable
      if (
        !existingHighlight ||
        (highlight.createdAt &&
          existingHighlight.createdAt &&
          new Date(highlight.createdAt) > new Date(existingHighlight.createdAt))
      ) {
        highlightMap.set(highlight.id, highlight);
      }
    });

    const deduplicated = Array.from(highlightMap.values());
    console.log(`Deduplicated to ${deduplicated.length} unique highlights by backend ID.`);
    return deduplicated;
  }
}

/**
 * Create highlighter instance using web-highlighter
 */
export const createHighlighter = async (
  container,
  onHighlightClick,
  onSelectionCreate,
  getCurrentColor,
  onHighlightRemove
) => {
  try {
    const wrapper = new WebHighlighterWrapper(
      container,
      onHighlightClick,
      onSelectionCreate,
      getCurrentColor,
      onHighlightRemove
    );

    const initialized = await wrapper.initialize();
    if (!initialized) {
      throw new Error("Failed to initialize Web-Highlighter wrapper");
    }

    return wrapper;
  } catch (error) {
    console.error("Failed to create Web-Highlighter wrapper:", error);
    return null;
  }
};

export { HighlightSource, WebHighlighterWrapper };
